<template>
  <div :class="{ 'closed': pinboardHidden }" class="dashboard-pinboard">
    <dd-button
      size="tiny"
      type="capsule"
      color="light-orange"
      class="dashboard-pinboard__toggle"
      @click="togglePinboard"
    >
      <span>{{togglePinboardLabel}}</span>
      <i :class="togglePinboardIcon" class="ddi" />
    </dd-button>
    <transition name="pinboard">
      <div v-if="!pinboardHidden">  
        <loading-placeholder v-if="isLoading" :lines="5" />
        <template v-else>
          <el-tooltip placement="bottom">
            <template #content>
              <div v-if="showMailboxes">On any task or conversation, click the <i class="ddi ddi-pin"/> button on the upper-right to pin it to this board.
              </div>
              <div v-else>On any task, click the <i class="ddi ddi-pin"/> button on the upper-right to pin it to this board.
              </div>
            </template>
            <i class="ddi ddi-info" />
          </el-tooltip>
          <transition-group name="pinned" class="dashboard-pinboard__pinned-items marker--dashboard-pinboard--pinned-items" tag="div">
            <template v-if="hasPinnedItems">
              <div
                v-for="item in pinnedItems"
                :key="item.id" 
                :class="{ 'convo' : item.isConversation }" 
                class="item" 
                name="pinned" 
              >
                <router-link :to="getItemRoute(item)">
                  <img class="pin" src="./img/icon-pin.svg" >
                  <i class="ddi ddi-close unpin-button" @click.prevent="removePinnedItem(item)" />
                  <div class="title"><strong>#{{item.refNumber}}:</strong> {{item.title}}</div>
                  <div class="entity">{{item.entity.name}}</div>
                  <div class="meta">
                    <avatar :size="20" :person="item.assignee" /> 
                    <priority-chicklet :priority="item.priority" hide-icon />
                    <status-chicklet :status="item.status"  />
                    <due-date-chicklet :due-date="item.dueDate"  />
                  </div>
                </router-link>
              </div>   
            </template>
            <div v-if="!hasNonSamplePinnedItems" key="item-placeholder" class="item-placeholder" name="pinned">
              <img class="pin" src="./img/icon-pin.svg" >
              <p v-if="showMailboxes">
                On any task or conversation, click the <i class="ddi ddi-pin"/> button to pin it to this board.
              </p>
              <p v-else>
                On any task, click the <i class="ddi ddi-pin"/> button to pin it to this board.
              </p>
            </div> 
          </transition-group>
        </template>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import * as routerNames from '@/services/router/router.names'
import {
  ContentPlaceholders,
  ContentPlaceholdersText,
} from '@/components/global/content-placeholders'

/* Mixins */

/* Components */

/* Helpers */
import LocalStore from '@/helpers/localstore'
import RouteAssistant from '@/helpers/route-assistant'

/* Data types */
import { StatusTypeValues } from '@/types'

/* API */
import DashboardAPI from '@/api/modules/dashboard.api'
import { IDashboardOutputDashboardItem } from '@/api/types/response.types'

export default defineComponent({
  name: 'DashboardPinboard',

  components: {
    ContentPlaceholders,
    ContentPlaceholdersText,
  },

  data() {
    return {
      StatusTypeValues,
      pinnedItems: [] as IDashboardOutputDashboardItem[],
      routerNames,
      isLoading: true,
      navPanels: [] as any[],
      pinboardHidden: false,
    }
  },

  computed: {
    hasPinnedItems(): boolean {
      return this.pinnedItems.length > 0
    },
    togglePinboardLabel(): string {
      return this.pinboardHidden ? 'Show your pinboard' : 'Hide'
    },
    togglePinboardIcon(): string {
      return this.pinboardHidden ? 'ddi-caret-down' : 'ddi-caret-up'
    },
    hasNonSamplePinnedItems(): boolean {
      return (
        this.hasPinnedItems &&
        this.pinnedItems.some(
          p => !this.$permissions.isSampleInternalProjectID(p.entity.id),
        )
      )
    },
    showMailboxes(): boolean {
      return this.$permissions.isNotGuest
    },
  },

  async created() {
    try {
      this.pinboardHidden = LocalStore.dashboardHidePinboard
      this.isLoading = true
      await this.getPinnedItems()
      this.isLoading = false
    } catch (error) {
      this.$error.emit(error)
    }
  },

  methods: {
    async getPinnedItems() {
      try {
        const response = await DashboardAPI.get_PinnedItems()
        this.pinnedItems = response.listItems
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async removePinnedItem(item: IDashboardOutputDashboardItem) {
      try {
        this.pinnedItems = this.pinnedItems.filter(pi => pi.id !== item.id)
        await DashboardAPI.delete_DeletePinnedItem(item.id)
      } catch (error) {
        this.$error.emit(error, true)
        this.getPinnedItems()
      }
    },
    limitedCountText(count: number): string {
      return count < 100 ? `${count}` : '99+'
    },
    togglePinboard() {
      this.pinboardHidden = !this.pinboardHidden
      LocalStore.dashboardHidePinboard = this.pinboardHidden
    },
    getItemRoute(item: IDashboardOutputDashboardItem) {
      if (item.isConversation) {
        return RouteAssistant.getConversationRoute(
          this.$route.params.accountId,
          item.entity.id,
          item.id,
          item.entity.name,
          item.refNumber,
          item.title,
        )
      } else {
        return RouteAssistant.getTaskRoute(
          this.$route.params.accountId,
          item.entity.id,
          item.id,
          this.$permissions,
          item.entity.name,
          item.refNumber,
          item.title,
        )
      }
    },
  },
})
</script>

<style lang="scss">
.dashboard-pinboard {
  display: flex;
  position: relative;
  margin-top: 0px;
  margin-bottom: 24px;

  .ddi-info {
    position: absolute;
    right: 10px;
    top: 10px;
    color: var(--color-text-third);
  }

  &.closed {
    border-bottom: $border--dotted-light;
  }

  &__toggle {
    position: absolute;
    bottom: -14px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1;

    .ddi {
      padding-left: 4px !important;
    }
  }

  .pinboard-enter-active {
    animation: pinboard-bounce-in 0.5s;
  }

  .pinboard-leave-active {
    animation: pinboard-bounce-in 0.5s reverse;
  }

  @keyframes pinboard-bounce-in {
    0% {
      transform: scale(0);
    }
    50% {
      transform: scale(1.01);
    }
    100% {
      transform: scale(1);
    }
  }

  .pinned-enter-active {
    animation: pinned-bounce-in 0.3s reverse;
  }

  .pinned-leave-active {
    animation: pinned-bounce-in 0.3s;
  }

  @keyframes pinned-bounce-in {
    0% {
      opacity: 1;
      transform: scale(1) rotate(-0.8deg);
    }
    50% {
      opacity: 0.9;
      transform: scale(1.01) rotate(-0.8deg);
    }
    100% {
      opacity: 0;
      transform: scale(0) rotate(-0.8deg);
    }
  }

  &.pinboard-hidden {
    padding-bottom: 0;
    margin-bottom: 0;
    border-bottom: none;
    top: -32px;
    z-index: 1050;

    @include media-breakpoint-down(sm) {
      top: -12px;
    }
  }

  > div {
    display: flex;
    flex-direction: column;
    align-items: center;
    background: url('./img/texture.png');
    background-color: var(--color-dashboard-pinboard);
    border: solid 1px var(--color-dashboard-pinboard);
    border-radius: 8px;
    padding: 40px 20px;
    width: 100%;

    h4 {
      color: $color--orange;
      padding-bottom: 10px;
    }
  }

  &__pinned-items {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    @include flex-row-vertical-spacing-on-wrap(12px);

    @include media-breakpoint-up(sm) {
      @include flex-row-horizontal-spacing(8px);
    }

    @include media-breakpoint-down(xs) {
      flex-direction: column;
      align-items: center;
    }

    > div {
      position: relative;
      border: $border--solid-extra-light;
      border-radius: 2px;
      padding: 21px 12px 12px 12px;

      .pin {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        top: -10px;
        opacity: 0.9;
      }

      &.item {
        padding-top: 30px;
        transform: rotate(-0.8deg);
        width: 24%;
        background-color: var(--color-background-header);
        border-top: solid 3px $color--blue;
        cursor: pointer;

        @include media-breakpoint-down(xs) {
          transform: rotate(0);
        }

        @include media-breakpoint-down(lg) {
          width: 40%;
        }

        @include media-breakpoint-down(xs) {
          width: 100%;
        }
        @include standard-box-shadow-with-hover;

        a:hover {
          text-decoration: none;
        }

        &.convo {
          border-top: solid 3px $mailboxes--theme-color;
        }

        .unpin-button {
          position: absolute;
          padding-top: 8px;
          padding-right: 8px;
          top: 0px;
          right: 0px;
          font-size: 14px;
          @include clickable-gray-color-states;
        }

        .title {
          @include lettering--p-sm;
          margin-bottom: 4px;
          @include multi-line-ellipsis(2);
        }

        .entity {
          @include lettering--p-sm--no-color;
          @include single-line-ellipsis;
          color: var(--color-text-third);
          margin-bottom: 10px;
        }

        .meta {
          display: flex;
          flex-wrap: wrap;
          @include flex-row-horizontal-spacing(4px);
          @include flex-row-vertical-spacing-on-wrap(4px);

          > * {
            @include single-line-ellipsis;
          }
        }
      }

      &.item-placeholder {
        max-width: 260px;
        transform: rotate(-0.8deg);
        @include media-breakpoint-down(sm) {
          transform: rotate(0);
        }
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100px;
        cursor: default;
        border-radius: 8px;
        border: dashed 3px $color--orange-40;
        opacity: 1;

        p {
          text-align: center;
          color: var(--color-text-dashboard-pinboard);
          font-weight: $bold;
        }
      }
    }
  }
}
</style>
